import { useCallback, useState, useEffect, useRef } from 'react';
import { useQuoteBuilderView } from '@paradigm/features/src/unified-rfqs/ui/grfq/quote-builder/view';
import { parseQtyToClearingUnities } from '@paradigm/features/src/unified-rfqs/ui/rfqs/quotes-functions';

import {
  Grfq,
  GrfqQuote,
} from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { GrfqQuoteV2CreateResp } from '@paradigm/features/src/unified-rfqs/repositories/grfq-types';
import { FormattingFn } from '@paradigm/features/src/unified-rfqs/ui/types';

import { toastQuote } from '#/components/UnifiedRfqs/toast';
import { useGrfqRfqQuotes } from '@paradigm/features/src/unified-rfqs/contexts/grfqs';
import { useOBV1APIFeatureFlag } from '@paradigm/features/src/feature-flags/context';

type ToastQuoteData =
  | {
      id: GrfqQuote['id'];
      params: Parameters<typeof toastQuote>;
    }
  | undefined;

const ASYNC_CREATE_TOAST_TIMEOUT = 5000;

/**
 * Custom hook that handles toasting once a newly created quote is available on
 * the quote book.
 */
export const useCreatedQuoteToaster = (rfqId: Grfq['id']) => {
  const { side } = useQuoteBuilderView();
  const quotes = useGrfqRfqQuotes(rfqId);
  const isOBV1Enabled = useOBV1APIFeatureFlag();

  const [createSuccessToastData, setCreateSuccessToastData] =
    useState<ToastQuoteData>();
  const timeout = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    if (
      isOBV1Enabled &&
      createSuccessToastData !== undefined &&
      (side === 'BUY' ? quotes.bids : quotes.asks).some(
        (quote) => quote.id === createSuccessToastData.id,
      )
    ) {
      toastQuote(...createSuccessToastData.params);
      clearTimeout(timeout.current);
      setCreateSuccessToastData(undefined);
    }
  }, [isOBV1Enabled, createSuccessToastData, quotes, side]);

  const toastCreatedQuote = useCallback(
    (...params: Parameters<typeof getCreatedToastData>) => {
      const toastData = getCreatedToastData(...params);
      if (!isOBV1Enabled && toastData !== undefined) {
        toastQuote(...toastData.params);
      } else {
        // Errors are handled by the websocket handler. So just expire the toast
        timeout.current = setTimeout(() => {
          setCreateSuccessToastData(undefined);
        }, ASYNC_CREATE_TOAST_TIMEOUT);
        setCreateSuccessToastData(toastData);
      }
    },
    [isOBV1Enabled, setCreateSuccessToastData],
  );

  return { toastCreatedQuote };
};

export const getCreatedToastData = (
  rfq: Grfq,
  responseQuote: GrfqQuote | GrfqQuoteV2CreateResp,
  formatPrice: FormattingFn<string>,
  formattedInputQuantity: string,
): ToastQuoteData => {
  // On OB original_quantity field:
  // In V1 API, we use response echoed value converted to display value
  // In V2 API, we use the original client-side display value
  const responseOriginalQuantity =
    'original_quantity' in responseQuote
      ? responseQuote.original_quantity
      : undefined;
  const formattedOriginalQuantity =
    responseOriginalQuantity !== undefined && responseOriginalQuantity !== '0'
      ? parseQtyToClearingUnities(
          responseOriginalQuantity,
          rfq.product_codes[0]!,
        ).qty
      : formattedInputQuantity; // Already formatted by the input state.
  if (formattedOriginalQuantity === '0') return undefined;
  return {
    id: responseQuote.id,
    params: [
      {
        side: responseQuote.side,
        price: formatPrice(responseQuote.price),
        original_quantity: formattedOriginalQuantity,
      },
      rfq.description,
    ],
  };
};
